import { Component, computed, inject, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '@libs/front/components/button/button.component';
import { PopupComponent } from '@libs/front/components/popup/popup.component';
import { toSignal } from '@angular/core/rxjs-interop';

import { KeycloakUtilisateurService } from 'src/app/services/keycloak/keycloak-utilisateur.service';
import { AuthenticationService } from 'src/app/services/authentication.service';

@Component({
  selector: 'app-profile-feature',
  standalone: true,
  imports: [CommonModule, TranslateModule, ButtonComponent, PopupComponent],
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
})
export class ProfileFeatureComponent {

  private readonly keycloakService = inject(KeycloakUtilisateurService);
  private readonly authService = inject(AuthenticationService);

  private readonly userProfile = toSignal(
    this.keycloakService.userProfile$,
    { initialValue: this.keycloakService.userProfile ?? null }
  );

  readonly initials = computed(() => this.userProfile()?.firstName?.charAt(0)?.toUpperCase() ?? '');
  readonly fullName = computed(() =>
    `${this.userProfile()?.firstName ?? ''} ${this.userProfile()?.lastName ?? ''}`.trim()
  );
  readonly email    = computed(() => this.userProfile()?.email ?? '');
  readonly licences = computed(() => this._buildLicences());
  isAvatarPopupOpen = false;
  isDragOver = false;
  selectedFile: File | null = null;
  selectedFileUrl: string | null = null;

  @ViewChild('fileInput') private readonly fileInputRef?: ElementRef<HTMLInputElement>;

  copyEmail(): void {
    const mail = this.email();
    if (mail) {
      navigator.clipboard.writeText(mail);
    }
  }

  resetPassword(): void {
    // Sends user to Keycloak account console for password change
    const accountUrl = this.keycloakService.keycloakInstance?.createAccountUrl?.();
    if (accountUrl) {
      window.open(accountUrl, '_blank', 'noopener,noreferrer');
    }
  }

  logout(): void {
    this.authService.logout();
  }

  private _buildLicences(): string[] {
    const roles = this.keycloakService.allRoles;
    const mapping: { role: string; label: string }[] = [
      { role: 'explore_app_user',  label: 'EXPLORE™'    },
      { role: 'expertab_app_user', label: 'EXPERTAB™'   },
      { role: 'vibradata_user',    label: 'VIBRADATA®'  },
      { role: 'expertir_app_user', label: 'EXPERTIR®'   },
    ];
    return mapping
      .filter(m => roles.includes(m.role))
      .map(m => m.label);
  }
  
  openAvatarPopup(): void {
    this.isAvatarPopupOpen = true;
  }

  closeAvatarPopup(): void {
    this.isAvatarPopupOpen = false;
    this._revokeFileUrl();
    this.selectedFile = null;
    this.selectedFileUrl = null;
    this.isDragOver = false;
  }

  triggerFileInput(): void {
    this.fileInputRef?.nativeElement.click();
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this._setFile(input.files[0]);
      input.value = '';
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(): void {
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files?.length) {
      this._setFile(files[0]);
    }
  }

  saveAvatar(): void {
    if (!this.selectedFile) return;
    // TODO: implement upload logic
    this.closeAvatarPopup();
  }

  private _setFile(file: File): void {
    const allowed = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowed.includes(file.type) || file.size > 4 * 1024 * 1024) return;
    this._revokeFileUrl();
    this.selectedFile = file;
    this.selectedFileUrl = file.type.startsWith('image/')
      ? URL.createObjectURL(file)
      : null;
  }

  private _revokeFileUrl(): void {
    if (this.selectedFileUrl) {
      URL.revokeObjectURL(this.selectedFileUrl);
    }
  }
}
